// German alphabet for the cipher: A-Z + Ä, Ö, Ü (29 letters)
export const GERMAN_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÜ';

export interface CipherMap {
  encode: Record<string, string>; // plaintext → ciphertext
  decode: Record<string, string>; // ciphertext → plaintext
}

/**
 * Generates a random derangement (permutation where no element maps to itself)
 * over the German alphabet.
 */
export function generateCipher(): CipherMap {
  const letters = GERMAN_ALPHABET.split('');
  let shuffled: string[];

  // Keep shuffling until we get a derangement (no fixed points)
  do {
    shuffled = [...letters];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
  } while (shuffled.some((c, i) => c === letters[i]));

  const encode: Record<string, string> = {};
  const decode: Record<string, string> = {};

  letters.forEach((letter, i) => {
    encode[letter] = shuffled[i];
    encode[letter.toLowerCase()] = shuffled[i].toLowerCase();
    decode[shuffled[i]] = letter;
    decode[shuffled[i].toLowerCase()] = letter.toLowerCase();
  });

  return { encode, decode };
}

/**
 * Normalizes a German quote: replaces ß with ss, keeps umlauts.
 */
export function normalizeText(text: string): string {
  return text.replace(/ß/g, 'ss').replace(/ẞ/g, 'SS');
}

/**
 * Encodes a plaintext string using the cipher map.
 * Non-alphabetic characters pass through unchanged.
 */
export function encodeText(text: string, cipher: CipherMap): string {
  return text
    .split('')
    .map((ch) => cipher.encode[ch] ?? ch)
    .join('');
}

/**
 * Returns the set of unique cipher letters used in the encoded text.
 */
export function getUsedCipherLetters(encodedText: string): Set<string> {
  const used = new Set<string>();
  for (const ch of encodedText) {
    if (GERMAN_ALPHABET.includes(ch.toUpperCase())) {
      used.add(ch.toUpperCase());
    }
  }
  return used;
}

/**
 * Returns true if the text contains at least one word shorter than minLength letters.
 * Used to filter out quotes where every word is long (hard to solve).
 */
export function hasShortWord(text: string, minLength = 4): boolean {
  const words = normalizeText(text).split(/[^A-Za-zÄÖÜäöü]+/).filter(Boolean);
  return words.some((w) => w.length < minLength);
}

/**
 * Checks if all letters are correctly filled in (puzzle solved).
 */
export function isSolved(
  userMapping: Record<string, string>,
  cipher: CipherMap,
  usedCipherLetters: Set<string>
): boolean {
  for (const cipherLetter of usedCipherLetters) {
    const correctPlain = cipher.decode[cipherLetter];
    if (!correctPlain) return false;
    if (userMapping[cipherLetter]?.toUpperCase() !== correctPlain.toUpperCase()) return false;
  }
  return true;
}
